import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { API_BASE_URI } from "../../http/api";
import { Collection } from "../../http/collection";
import { Store } from "@ngxs/store";
import { Filter, FilterBuilder } from "../../http/filter";
import { Observable } from "rxjs";
import { DetailRequest, DetailRequestBuilder } from "../../http/detail";
import { tap } from "rxjs/operators";
import { AdvancedReport, AdvancedReportTemplate, AnyReportTemplate, NewReportTemplate, ReportPageTemplate } from "@core/data/types/advanced-report";

export class AdvancedReportTemplateDeleted {
    static readonly type = "[AdvancedReportTemplateService] Template Deleted";
    constructor(public template: AnyReportTemplate){}
}

export class AdvancedReportTemplateUpdated {
    static readonly type = "[AdvancedReportTemplateService] Template Updated";
    constructor(public template: AnyReportTemplate){}
}

export class AdvancedReportTemplateCreated {
    static readonly type = "[AdvancedReportTemplateService] Template Created";
    constructor(public template: AnyReportTemplate){}
}

export class AdvancedReportTemplateApplied {
    static readonly type = "[AdvancedReportTemplateService] Template Applied";
    constructor(public report: AdvancedReport){}
}

@Injectable()
export class AdvancedReportTemplateService {

    constructor(
        @Inject(API_BASE_URI) private _baseUri: string,
        private _http: HttpClient,
        private _store: Store,
    ) {}

    get<T extends AnyReportTemplate = AnyReportTemplate>(key: string, detail?: DetailRequest): Observable<T> {
        const params = detail ? DetailRequestBuilder.queryParams(detail) : {};
        return this._http.get<T>(`${this._baseUri}/advanced-report-template/${key}`, { params });
    }

    query<T extends AnyReportTemplate = AnyReportTemplate>(filter: Filter, detail?: DetailRequest): Observable<Collection<T>> {
        let params = {
            ...FilterBuilder.queryParams(filter),
            ...DetailRequestBuilder.queryParams(detail),
        }

        return this._http.get<Collection<T>>(`${this._baseUri}/advanced-report-template`, { params });
    }

    create<T extends AnyReportTemplate = AnyReportTemplate>(model: NewReportTemplate<T>): Observable<T> {
        return this._http.post<T>(`${this._baseUri}/advanced-report-template`, model)
            .pipe(
                tap(
                    template => this._store.dispatch(new AdvancedReportTemplateCreated(template))
                )
            );
    }

    update<T extends AnyReportTemplate = AnyReportTemplate>(key: string, model: T) {
        return this._http.put<T>(`${this._baseUri}/advanced-report-template/${key}`, model)
            .pipe(
                tap(
                    template => this._store.dispatch(new AdvancedReportTemplateUpdated(template))
                )
            );
    }

    delete<T extends AnyReportTemplate = AnyReportTemplate>(key: string) {
        return this._http.delete<T>(`${this._baseUri}/advanced-report-template/${key}`)
            .pipe(
                tap(
                    template => this._store.dispatch(new AdvancedReportTemplateDeleted(template))
                )
            );
    }

    apply(templateKey: string, reportKey: string) {
       return this._http.put<any>(`${this._baseUri}/advanced-report-template/apply/${templateKey}/${reportKey}`, { })
            .pipe(
                tap(
                    report => this._store.dispatch(new AdvancedReportTemplateApplied(report))
                )
            );
    }

}